import {Outlet} from 'react-router-dom';
import {ReactNode, useEffect, useState} from 'react';
import {
  Page,
  Main,
  Body,
  ContentArea,
} from '@cvent/carina/components/Templates';
import {globalUIContext, useGlobalUI} from '../utils/global-ui-helpers';
import {SOCIAL_TABLES_TITLE} from '../../constants/page-title';
import {RedesignModal} from '../components/modal/RedesignModal';
import {Footer} from './Footer';
import {Navbar} from './Navbar';

const BaseLayout = ({
  backgroundImage,
  showFooter = true,
  showNavbar = true,
  transparentBackground = false,
  children,
}: {
  backgroundImage?: string;
  showFooter?: boolean;
  showNavbar?: boolean;
  transparentBackground?: boolean;
  children?: ReactNode;
}) => {
  const globalUI = useGlobalUI();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const contentAreaClassName = [
    'flex',
    'flex-col',
    'h-full',
    'overflow-hidden',
    'relative',
  ].join(' ');

  // Better placeholder colors that match the background images
  const getPlaceholderColor = () => {
    if (!backgroundImage) return 'transparent';
    if (backgroundImage.includes('home-bg')) return '#f0f4f8'; // Light blue-gray for home
    if (backgroundImage.includes('layout-bg')) return '#f5f7fa'; // Slightly warmer gray for forms
    return '#f5f5f5'; // Default light gray
  };

  // Preload background image and only show when fully loaded
  useEffect(() => {
    if (backgroundImage && !globalUI.isMobile) {
      let isCancelled = false;
      setIsImageLoaded(false);

      const img = new Image();
      img.src = backgroundImage;

      img.onload = () => {
        if (!isCancelled) {
          setIsImageLoaded(true);
        }
      };

      img.onerror = () => {
        // On error, keep isImageLoaded false so placeholder color shows
        // No need to call setIsImageLoaded(false) since it's already false
      };

      // If image is already cached, it loads instantly
      if (img.complete && !isCancelled) {
        setIsImageLoaded(true);
      }

      return () => {
        isCancelled = true;
        img.onload = null;
        img.onerror = null;
      };
    }
    return undefined;
  }, [backgroundImage, globalUI.isMobile]);

  return (
    <Page>
      <Main>
        <globalUIContext.Provider value={globalUI}>
          <div
            id="screen-reader-announcer"
            role="region"
            className="screen-reader-only"
            aria-live="assertive"
            aria-atomic="true"
          >
            Navigated to {SOCIAL_TABLES_TITLE}
          </div>
          {showNavbar ? (
            <Navbar transparentBackground={transparentBackground} />
          ) : null}
          <Body className="flex-grow">
            <ContentArea className={contentAreaClassName}>
              {globalUI.isMobile ? null : (
                <div
                  className={`fixed inset-0 top-0 pointer-events-none ${
                    isImageLoaded ? 'animate-fadeIn' : ''
                  }`}
                  style={{
                    ...(backgroundImage &&
                      isImageLoaded && {
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundPosition: 'center',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                      }),
                    backgroundColor: getPlaceholderColor(),
                    zIndex: 0,
                  }}
                />
              )}
              <div
                className="flex flex-col flex-grow overflow-auto relative"
                style={{zIndex: 1}}
              >
                <div className="flex-grow flex flex-col">
                  {children ?? <Outlet />}
                </div>
                {showFooter ? <Footer /> : null}
              </div>
            </ContentArea>
          </Body>
          <RedesignModal />
        </globalUIContext.Provider>
      </Main>
    </Page>
  );
};

export default BaseLayout;
